/**
 * VP-313 — logika logowania przez Google, gdy konto ma włączone 2FA (TOTP).
 *
 * `POST /api/auth/google` odpowiada na jeden `idToken` na cztery sposoby:
 *  - `200 { token, user }`       → zalogowany,
 *  - `200 { requires2FA: true }` → konto ma TOTP, trzeba dosłać kod,
 *  - `403`                       → kod odrzucony, `idToken` dalej ważny → poproś o kod jeszcze raz,
 *  - `401`                       → `idToken` nieważny/wygasł → nowy popup Google.
 *
 * Zły kod jest błędem 4xx celowo — inaczej `googleAuthRateLimiter`
 * (`skipSuccessfulRequests`) nie zliczyłby próby i TOTP byłby otwarty na brute-force.
 *
 * Rozstrzyganie siedzi tutaj, a nie w komponencie, żeby dało się je przetestować bez DOM-u.
 */

export type GoogleUser = {
  id: string;
  username: string;
  email: string;
};

export type GoogleAuthenticatedResponse = {
  user: GoogleUser;
  token: string;
  isNewUser?: boolean;
  requiresOnboarding?: boolean;
};

export type GoogleLoginRequest = {
  idToken: string;
  totpCode?: string;
  referralCode?: string;
};

export type GoogleLoginOutcome =
  | { status: "authenticated"; data: GoogleAuthenticatedResponse }
  /** `invalidCode` = poprzedni kod był zły; `false` przy pierwszym pytaniu o kod. */
  | { status: "totp_required"; invalidCode: boolean }
  | { status: "google_session_expired" };

/** `apiRequest` rzuca `Error("<status>: …")` — to jedyny nośnik statusu po stronie klienta. */
const hasStatus = (error: unknown, status: number): boolean =>
  error instanceof Error && error.message.startsWith(`${status}:`);

export async function submitGoogleLogin(params: {
  post: (body: GoogleLoginRequest) => Promise<unknown>;
  idToken: string;
  totpCode?: string;
  referralCode?: string;
}): Promise<GoogleLoginOutcome> {
  const { post, idToken, totpCode, referralCode } = params;

  let raw: unknown;
  try {
    raw = await post({
      idToken,
      ...(totpCode ? { totpCode } : {}),
      ...(referralCode ? { referralCode } : {}),
    });
  } catch (error) {
    // 403 = idToken w porządku, kod TOTP odrzucony → pytamy o kod jeszcze raz.
    if (hasStatus(error, 403)) return { status: "totp_required", invalidCode: true };
    if (hasStatus(error, 401)) return { status: "google_session_expired" };
    throw error;
  }

  const data = (raw ?? {}) as Partial<GoogleAuthenticatedResponse> & {
    requires2FA?: boolean;
    invalidCode?: boolean;
  };

  if (data.requires2FA) {
    return { status: "totp_required", invalidCode: data.invalidCode === true };
  }
  if (!data.token || !data.user) {
    throw new Error("google-login: response without token/user");
  }
  return { status: "authenticated", data: data as GoogleAuthenticatedResponse };
}
